import { useState } from 'react'
import { createPortal } from 'react-dom'
import userService from '../services/userService'
import Swal from 'sweetalert2'
import styles from './ChangePasswordModal.module.css'

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  if (!isOpen) return null

  console.log('ChangePasswordModal rendered, isOpen:', isOpen)

  const handleClose = () => {
    setOldPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setErrors({
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    })
    onClose()
  }

  const validateField = (name, value) => {
    let error = ''
    
    if (name === 'oldPassword') {
      if (!value || value.trim() === '') {
        error = 'Old password is required'
      }
    } else if (name === 'newPassword') {
      if (!value || value.trim() === '') {
        error = 'New password is required'
      } else if (value.length < 6) {
        error = 'Password must be at least 6 characters'
      }
    } else if (name === 'confirmPassword') {
      if (!value || value.trim() === '') {
        error = 'Please confirm your password'
      } else if (value !== newPassword) {
        error = 'Passwords do not match'
      }
    }
    
    return error
  }

  const handleOldPasswordChange = (e) => {
    const value = e.target.value
    setOldPassword(value)
    setErrors(prev => ({
      ...prev,
      oldPassword: validateField('oldPassword', value)
    }))
  }

  const handleNewPasswordChange = (e) => {
    const value = e.target.value
    setNewPassword(value)
    setErrors(prev => ({
      ...prev,
      newPassword: validateField('newPassword', value),
      confirmPassword: confirmPassword ? validateField('confirmPassword', confirmPassword) : prev.confirmPassword
    }))
  }

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value
    setConfirmPassword(value)
    setErrors(prev => ({
      ...prev,
      confirmPassword: validateField('confirmPassword', value)
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate all fields first
    const oldPasswordError = validateField('oldPassword', oldPassword)
    const newPasswordError = validateField('newPassword', newPassword)
    const confirmPasswordError = validateField('confirmPassword', confirmPassword)

    // Set all errors
    setErrors({
      oldPassword: oldPasswordError,
      newPassword: newPasswordError,
      confirmPassword: confirmPasswordError
    })

    // Check if there are any validation errors
    if (oldPasswordError || newPasswordError || confirmPasswordError) {
      // Swal.fire({
      //   icon: 'error',
      //   title: 'Validation Error',
      //   text: 'Please fix all errors before submitting',
      //   toast: true,
      //   position: 'top-end',
      //   showConfirmButton: false,
      //   timer: 3000
      // })
      return
    }

    setLoading(true)
    try {
      const payload = {
        newPassword: newPassword,
        oldPassword: oldPassword 
      }

      const response = await userService.updateLordsPassword(payload)

      if (response.success) {
        Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Password changed successfully',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000
          })
        handleClose()
      } else {
        const errorMessage = response.message || response.error || 'Failed to change password'
        
        // Check if error is related to old password
        if (errorMessage.toLowerCase().includes('old password') || errorMessage.toLowerCase().includes('current password') || errorMessage.toLowerCase().includes('incorrect password')) {
          setErrors(prev => ({
            ...prev,
            oldPassword: errorMessage
          }))
        } else {
          // Set general error on old password field if it's a validation error
          setErrors(prev => ({
            ...prev,
            oldPassword: errorMessage
          }))
        }
        
        Swal.fire({
            icon: 'error',
            title: 'Error',
            text: errorMessage,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000
          })
      }
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'An error occurred'
        
        // Check if error is related to old password
        if (errorMessage.toLowerCase().includes('old password') || errorMessage.toLowerCase().includes('current password') || errorMessage.toLowerCase().includes('incorrect password')) {
          setErrors(prev => ({
            ...prev,
            oldPassword: errorMessage
          }))
        } else {
          // Set general error on old password field
          setErrors(prev => ({
            ...prev,
            oldPassword: errorMessage
          }))
        }
        
        Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      })
    } finally {
      setLoading(false)
    }
  }

  return createPortal(
    <div className={styles.modalOverlay}>
      <div className={styles.modalDialog}>
        <div className={styles.modalContent}>
          {/* Close Button */}
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
          
          {/* Body */}
          <div className={styles.modalBody}>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <input
                  type="password"
                  value={oldPassword}
                  onChange={handleOldPasswordChange}
                  onBlur={(e) => {
                    const error = validateField('oldPassword', e.target.value)
                    setErrors(prev => ({ ...prev, oldPassword: error }))
                  }}
                  placeholder="Enter old password"
                  className="w-full px-3 py-2 text-[1rem] text-[#495057] bg-white border border-gray-300  focus:outline-none focus:border-blue-500"
                />
                {errors.oldPassword && (
                  <div className={styles.errorMessage}>
                    {errors.oldPassword}
                  </div>
                )}
              </div>
              
              <div>
                <input
                  type="password"
                  value={newPassword}
                  onChange={handleNewPasswordChange}
                  onBlur={(e) => {
                    const error = validateField('newPassword', e.target.value)
                    setErrors(prev => ({ ...prev, newPassword: error }))
                  }}
                  placeholder="Enter new password"
                  className="w-full px-3 py-2 text-[1rem] text-[#495057] bg-white border border-gray-300  focus:outline-none focus:border-blue-500"
                />
                {errors.newPassword && (
                  <div className={styles.errorMessage}>
                    {errors.newPassword}
                  </div>
                )}
              </div>

              <div>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  onBlur={(e) => {
                    const error = validateField('confirmPassword', e.target.value)
                    setErrors(prev => ({ ...prev, confirmPassword: error }))
                  }}
                  placeholder="Enter confirm password"
                  className="w-full px-3 py-2 text-[1rem] text-[#495057] bg-white border border-gray-300  focus:outline-none focus:border-blue-500"
                />
                {errors.confirmPassword && (
                  <div className={styles.errorMessage}>
                    {errors.confirmPassword}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end pt-4 -b gap-4">
                <button
                  className="text-green-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                  type="button"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  className="bg-transparent text-white active:bg-blue-600 font-bold uppercase text-sm px-6 py-2 shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? 'Wait...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default ChangePasswordModal
